import { useMemo } from 'react'
import type { NodeFormConfig, NodeFormData } from '../components/nodes/NodeForm'
import type { RegisterOptions } from 'react-hook-form'

interface UseNodeFormValidationProps {
  config: NodeFormConfig
}

// Define error object type
interface FieldErrors {
  [key: string]: {
    message?: string
  }
}

// Define the specific fields we have validation for
type ValidatedFields = 'name' | 'description' | 'email' | 'url' | 'password' | 'confirmPassword'

export function useNodeFormValidation({ config }: UseNodeFormValidationProps) {
  const validationRules = useMemo(() => {
    return {
      name: {
        required: config.nameLabel.includes('*') ? 'Name is required' : false,
      },
      description: {
        required: config.descriptionRequired ? 'Description is required' : false,
      },
      email: {
        required: 'Email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address',
        },
      },
      url: {
        pattern: {
          value: /^https?:\/\/.+/,
          message: 'Please enter a valid URL starting with http:// or https://',
        },
      },
      password: {
        minLength: {
          value: 8,
          message: 'Password must be at least 8 characters long',
        },
      },
      confirmPassword: {
        validate: (value: string | undefined, formValues: NodeFormData) => {
          if (value !== formValues.password) {
            return 'Passwords do not match'
          }
          return true
        },
      },
    }
  }, [config])

  // Helper function to get validation rules for a specific field
  const getFieldValidation = <T extends ValidatedFields>(
    fieldName: T
  ): RegisterOptions<NodeFormData, T> => {
    return validationRules[fieldName] as RegisterOptions<NodeFormData, T>
  }

  // Helper function to check if a field is required
  const isFieldRequired = (fieldName: ValidatedFields): boolean => {
    const rules = validationRules[fieldName]
    return 'required' in rules ? Boolean(rules.required) : false
  }

  // Helper function to get error message for a field
  const getFieldErrorMessage = (
    fieldName: ValidatedFields,
    errors: FieldErrors
  ): string | undefined => {
    return errors[fieldName]?.message
  }

  return {
    validationRules,
    getFieldValidation,
    isFieldRequired,
    getFieldErrorMessage,
  }
}
